import React from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';

import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Undo2,
  Redo2,
  Link as LinkIcon,
  Unlink,
} from 'lucide-react';

import './RichTextEditor.css';

/**
 * WordPress-style rich text editor. Emits HTML to `onChange`.
 * Used for product / category / brand descriptions in admin.
 */
const RichTextEditor = ({ value, onChange, placeholder = 'Scrie descrierea...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Link.configure({ openOnClick: false, autolink: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    editorProps: {
      attributes: { class: 'rich-text-content' },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Keep editor content in sync when parent updates the value externally
  // (e.g., loading an existing product into the form).
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Introdu URL-ul:', previousUrl || '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url })
      .run();
  };

  const btnCls = (active) => (active ? 'active' : '');

  return (
    <div className="rich-text-editor" data-testid="rich-text-editor">
      <div className="rich-text-toolbar">
        <button
          type="button"
          title="Bold"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
          className={btnCls(editor.isActive('bold'))}
        ><Bold size={17} /></button>

        <button
          type="button"
          title="Italic"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
          className={btnCls(editor.isActive('italic'))}
        ><Italic size={17} /></button>

        <button
          type="button"
          title="Subliniat"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run(); }}
          className={btnCls(editor.isActive('underline'))}
        ><UnderlineIcon size={17} /></button>

        <button
          type="button"
          title="Tăiat"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleStrike().run(); }}
          className={btnCls(editor.isActive('strike'))}
        ><Strikethrough size={17} /></button>

        <span className="toolbar-divider" />

        <select
          value={
            editor.isActive('heading', { level: 1 }) ? 'h1'
              : editor.isActive('heading', { level: 2 }) ? 'h2'
              : editor.isActive('heading', { level: 3 }) ? 'h3'
              : 'p'
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'p') editor.chain().focus().setParagraph().run();
            if (v === 'h1') editor.chain().focus().toggleHeading({ level: 1 }).run();
            if (v === 'h2') editor.chain().focus().toggleHeading({ level: 2 }).run();
            if (v === 'h3') editor.chain().focus().toggleHeading({ level: 3 }).run();
          }}
          className="heading-select"
          title="Mărime text"
        >
          <option value="p">Normal</option>
          <option value="h1">Titlu 1</option>
          <option value="h2">Titlu 2</option>
          <option value="h3">Titlu 3</option>
        </select>

        <span className="toolbar-divider" />

        <button
          type="button"
          title="Aliniere stânga"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('left').run(); }}
          className={btnCls(editor.isActive({ textAlign: 'left' }))}
        ><AlignLeft size={17} /></button>

        <button
          type="button"
          title="Centrare"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('center').run(); }}
          className={btnCls(editor.isActive({ textAlign: 'center' }))}
        ><AlignCenter size={17} /></button>

        <button
          type="button"
          title="Aliniere dreapta"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('right').run(); }}
          className={btnCls(editor.isActive({ textAlign: 'right' }))}
        ><AlignRight size={17} /></button>

        <button
          type="button"
          title="Justificat"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setTextAlign('justify').run(); }}
          className={btnCls(editor.isActive({ textAlign: 'justify' }))}
        ><AlignJustify size={17} /></button>

        <span className="toolbar-divider" />

        <button
          type="button"
          title="Listă"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
          className={btnCls(editor.isActive('bulletList'))}
        ><List size={18} /></button>

        <button
          type="button"
          title="Listă numerotată"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}
          className={btnCls(editor.isActive('orderedList'))}
        ><ListOrdered size={18} /></button>

        <span className="toolbar-divider" />

        <button
          type="button"
          title="Adaugă link"
          onMouseDown={(e) => { e.preventDefault(); setLink(); }}
          className={btnCls(editor.isActive('link'))}
        ><LinkIcon size={17} /></button>

        <button
          type="button"
          title="Elimină link"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetLink().run(); }}
          disabled={!editor.isActive('link')}
        ><Unlink size={17} /></button>

        <span className="toolbar-divider" />

        <button
          type="button"
          title="Înapoi"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().undo().run(); }}
          disabled={!editor.can().undo()}
        ><Undo2 size={17} /></button>

        <button
          type="button"
          title="Refă"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().redo().run(); }}
          disabled={!editor.can().redo()}
        ><Redo2 size={17} /></button>
      </div>

      <div className="rich-text-body">
        {!value && (
          <div className="rich-text-placeholder">{placeholder}</div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default RichTextEditor;
